'use client'

import { type ChangeEvent, type KeyboardEvent, useRef } from 'react'

import { FilterDropdown, FilterDropdownSize } from '@graftcode/design-system'

import { MEMBERS_INVITE_DIALOG_TEST_IDS } from '@common/common-test-ids.const'

import { EMAIL_PATTERN } from './members-invite-dialog.const'

import styles from './MembersInviteTagInput.module.scss'

type RoleOption = { id: string; label: string; type: 'item' }

type MembersInviteTagInputProps = {
  conflictMessage?: string | null
  disabled?: boolean
  emails: string[]
  emailInvalidMessage: string
  onEmailsChange: (emails: string[]) => void
  onRoleSelect: (id: string) => void
  placeholder?: string
  roleLabel: string
  roleOptions: RoleOption[]
}

export function MembersInviteTagInput({
  conflictMessage,
  disabled,
  emails,
  emailInvalidMessage,
  onEmailsChange,
  onRoleSelect,
  placeholder,
  roleLabel,
  roleOptions,
}: MembersInviteTagInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const email = emails[0] ?? ''
  const isEmailValid = email ? EMAIL_PATTERN.test(email) : false
  const showFormatValidationMessage = Boolean(email && !isEmailValid)
  const showConflictMessage = Boolean(email && isEmailValid && conflictMessage)
  const showValidationMessage = showFormatValidationMessage || showConflictMessage
  const validationMessage = showFormatValidationMessage ? emailInvalidMessage : (conflictMessage ?? '')

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
    }
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim()
    onEmailsChange(value ? [value] : [])
  }

  const handleContainerClick = () => {
    inputRef.current?.focus()
  }

  return (
    <div className={styles.field}>
      <div className={styles.container} onClick={handleContainerClick}>
        <div className={styles.inputArea}>
          <input
            name="members-invite-tag-input"
            ref={inputRef}
            type="text"
            className={styles.input}
            data-testid={MEMBERS_INVITE_DIALOG_TEST_IDS.emailInput}
            aria-invalid={showValidationMessage}
            aria-describedby={showValidationMessage ? 'members-invite-email-error' : undefined}
            value={email}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
          />
        </div>
        <FilterDropdown
          label={roleLabel}
          options={roleOptions}
          size={FilterDropdownSize.M}
          disabled={disabled}
          onSelect={onRoleSelect}
        />
      </div>
      {showValidationMessage ? (
        <p className={styles.errorMessage} id="members-invite-email-error">
          {validationMessage}
        </p>
      ) : null}
    </div>
  )
}
